import React, { useCallback, useState } from 'react';
import { useParams } from 'react-router';
import { useSelector } from 'react-redux';
import { FormatDateOptions, useIntl } from 'react-intl';
import { Column } from 'react-table';
import { computeText } from '@/locales/utils';
import { theme } from '@/core/theme';
import { SVGIcon, SVGIcons } from '@/components/atoms/Icon/SVGIcon';
import { TableRow } from '@/components/atoms/Table/Table.type';
import { BadgeProps } from '@/components/atoms/Badge/Badge';
import { fetchDataProps } from '@/components/atoms/Table';
import { ButtonProps } from '@/components/atoms/Button';
import {
    ArchiveMenuTiming,
    MenuOperation,
    MenuOperationState,
    MenuOperationType,
} from '@/services/innovorder/menu/menu.type';
import {
    useGetMenuOperationsMutation,
    useLazyGetArchivedMenuSignedUrlQuery,
} from '@/services/innovorder/menu/menu.endpoints';
import { getUserToken } from '@/redux/user';

import { type IconAndText } from '@/components/atoms/List/IconAndText';
import { downloadFileFromUrl } from '@/utils/file/utils';

const ARCHIVED_MENU_EXPIRATION_TIME = 1000 * 60 * 60 * 24 * 180; // 180 days

type ParamTypes = {
    brandId: string;
    restaurantId: string;
};

type MenuOperationListViewModel = {
    menuOperations: MenuOperation[];
    isLoading: boolean;
    headers: Column<TableRow>[];
    rows: TableRow[];
    fetchData: (props: fetchDataProps) => void;
    refreshData: () => void;
    fetchDataTotalCount: number;
    handleDownloadArchivedMenu: (menuOperationId: number, timing: ArchiveMenuTiming) => Promise<void>;
    downloadError: string | null;
    clearDownloadError: () => void;
};

export function useMenuOperationListVM(): MenuOperationListViewModel {
    const intl = useIntl();
    const { brandId: paramBrandId, restaurantId: paramRestaurantId } = useParams<ParamTypes>();
    const brandId = Number.parseInt(paramBrandId, 10);
    const restaurantId = Number.parseInt(paramRestaurantId, 10);
    const token = useSelector(getUserToken);

    const [getMenuOperations, { data, isLoading: isGetMenuOperationLoading }] = useGetMenuOperationsMutation();
    const { count = 0, data: menuOperations = [] } = data || {};
    const [currentFilter, setFilter] = useState({ offset: 0, limit: 10 });
    const [getArchivedMenuSignedUrl] = useLazyGetArchivedMenuSignedUrlQuery();
    const [downloadingIds, setDownloadingIds] = useState<Set<string>>(new Set());
    const [downloadError, setDownloadError] = useState<string | null>(null);

    const clearDownloadError = useCallback(() => setDownloadError(null), []);

    const fetchData = useCallback(
        ({ pageIndex = 0, pageSize = 0 }: fetchDataProps) => {
            const offset = pageSize * pageIndex;
            const limit = pageSize;

            setFilter({ offset, limit });
            getMenuOperations({ brandId, restaurantId, token, offset, limit });
        },
        [brandId, restaurantId, token, getMenuOperations, setFilter],
    );

    const refreshData = () => {
        getMenuOperations({ brandId, restaurantId, token, ...currentFilter });
    };

    const handleDownloadArchivedMenu = useCallback(
        async (menuOperationId: number, timing: ArchiveMenuTiming) => {
            const downloadKey = `${menuOperationId}-${timing}`;
            setDownloadingIds((prev) => new Set(prev).add(downloadKey));
            try {
                const result = await getArchivedMenuSignedUrl({ menuOperationId, timing, token }).unwrap();
                if (result?.signedUrl) {
                    downloadFileFromUrl(result.signedUrl);
                }
            } catch (error) {
                setDownloadError('menuoperation.download.error');
            } finally {
                setDownloadingIds((prev) => {
                    const next = new Set(prev);
                    next.delete(downloadKey);
                    return next;
                });
            }
        },
        [getArchivedMenuSignedUrl, token],
    );

    const getTypeBadge = (type: MenuOperationType): BadgeProps => {
        switch (type) {
            case 'update':
                return {
                    text: computeText(intl, 'menuoperation.type.update'),
                    backgroundColor: theme.color.lightBlue100,
                };
            case 'publish':
                return {
                    text: computeText(intl, 'menuoperation.type.publish'),
                    backgroundColor: theme.color.electricBlue,
                };
            case 'propagation':
                return {
                    text: computeText(intl, 'menuoperation.type.propagation'),
                    backgroundColor: theme.color.mint,
                };
            default:
                return {
                    text: computeText(intl, 'status.unknown'),
                    backgroundColor: theme.color.grey400,
                };
        }
    };

    const getOperationMessage = ({
        message,
        status,
        type,
        moduleMarketplaceId,
    }: MenuOperation): (string | IconAndText)[] => {
        const formattedMessage = message ?? computeText(intl, 'status.unknown');
        if (status === 'ERROR' && moduleMarketplaceId === null) {
            return [formattedMessage];
        }

        switch (type) {
            case 'update':
                return [`${computeText(intl, 'menuoperation.message.from')} ${formattedMessage}`];
            case 'publish':
                return [`${computeText(intl, 'menuoperation.message.to')} ${formattedMessage}`];
            case 'propagation':
                return [`${computeText(intl, 'menuoperation.message.from.brandMenu')} ${formattedMessage}`];
            default:
                return [formattedMessage];
        }
    };

    const getStatusBadge = (status: MenuOperationState): BadgeProps => {
        switch (status) {
            case 'NEW':
                return {
                    text: computeText(intl, 'status.new'),
                    backgroundColor: theme.color.mint,
                };
            case 'FILE_UPLOADED':
                return {
                    text: computeText(intl, 'status.file_uploaded'),
                    backgroundColor: theme.color.mint,
                };
            case 'PROCESSING':
                return {
                    text: computeText(intl, 'status.processing'),
                    backgroundColor: theme.color.mint,
                };
            case 'ERROR':
                return {
                    text: computeText(intl, 'status.error'),
                    backgroundColor: theme.color.bloodyMary,
                };
            case 'DONE':
                return {
                    text: computeText(intl, 'status.done'),
                    backgroundColor: theme.color.emerald,
                };
            default:
                return {
                    text: computeText(intl, 'status.unknown'),
                    backgroundColor: theme.color.grey400,
                };
        }
    };

    const headers = [
        {
            Header: computeText(intl, 'date.created'),
            accessor: 'createdAt',
            width: '10%',
        },
        {
            Header: computeText(intl, 'date.updated'),
            accessor: 'updatedAt',
            width: '10%',
        },
        {
            Header: computeText(intl, 'menuoperation.type'),
            accessor: 'type',
            width: '8%',
        },
        {
            Header: computeText(intl, 'menu.name'),
            accessor: 'name',
            width: '12%',
        },
        {
            Header: computeText(intl, 'message.message'),
            accessor: 'message',
            width: '22%',
        },
        {
            Header: computeText(intl, 'status.status'),
            accessor: 'status',
            width: '10%',
        },
        {
            Header: computeText(intl, 'menuoperation.actions'),
            accessor: 'actions',
            width: '28%',
        },
    ];

    const isMenuNotExpired = (createdAt: string) =>
        new Date(createdAt).getTime() + ARCHIVED_MENU_EXPIRATION_TIME > Date.now();

    const dateFormatOptions: FormatDateOptions = {
        hourCycle: 'h24',
        timeStyle: 'short',
        dateStyle: 'short',
    };

    const rows: TableRow[] = menuOperations.map((menuOperation) => {
        const showBeforeButton = menuOperation.archivedPreviousMenuPath && isMenuNotExpired(menuOperation.createdAt);
        const showAfterButton = menuOperation.archivedResultMenuPath && isMenuNotExpired(menuOperation.createdAt);

        const getActionButtons = (): ButtonProps[] => {
            const buttons: ButtonProps[] = [];

            if (showBeforeButton) {
                buttons.push({
                    children: (
                        <>
                            <SVGIcon icon={SVGIcons.EXPORTS} size={16} />
                            <span>{computeText(intl, 'menuoperation.action.download.before')}</span>
                        </>
                    ),
                    buttonType: 'primary',
                    onClick: () => handleDownloadArchivedMenu(menuOperation.menuOperationId, 'before'),
                    disabled: downloadingIds.has(`${menuOperation.menuOperationId}-before`),
                });
            }

            if (showAfterButton) {
                buttons.push({
                    children: (
                        <>
                            <SVGIcon icon={SVGIcons.EXPORTS} size={16} />
                            <span>{computeText(intl, 'menuoperation.action.download.after')}</span>
                        </>
                    ),
                    buttonType: 'outlinedPrimary',
                    onClick: () => handleDownloadArchivedMenu(menuOperation.menuOperationId, 'after'),
                    disabled: downloadingIds.has(`${menuOperation.menuOperationId}-after`),
                });
            }

            return buttons;
        };

        const actionButtons = getActionButtons();

        return {
            createdAt: {
                type: 'string',
                value: {
                    text: intl.formatDate(menuOperation.createdAt, dateFormatOptions),
                },
            },
            updatedAt: {
                type: 'string',
                value: {
                    text: intl.formatDate(menuOperation.updatedAt, dateFormatOptions),
                },
            },
            type: {
                type: 'badge',
                value: getTypeBadge(menuOperation.type),
            },
            name: {
                type: 'string',
                value: {
                    text: menuOperation.name,
                    status: menuOperation.status,
                    moduleMarketplaceId: menuOperation.moduleMarketplaceId,
                },
            },
            message: {
                type: 'list',
                value: {
                    listItems: getOperationMessage(menuOperation),
                    separator: '  ',
                },
            },
            status: {
                type: 'badge',
                value: getStatusBadge(menuOperation.status),
            },
            actions:
                actionButtons.length > 0
                    ? {
                          type: 'actionList',
                          value: actionButtons,
                      }
                    : {
                          type: 'empty',
                          value: '',
                      },
        };
    });

    return {
        menuOperations,
        isLoading: isGetMenuOperationLoading,
        fetchData,
        refreshData,
        headers,
        rows,
        fetchDataTotalCount: count,
        handleDownloadArchivedMenu,
        downloadError,
        clearDownloadError,
    };
}
